import React, { useState, useEffect, useContext, useRef } from "react";
import axios from "axios";
import dayjs from "dayjs";
import {
  Drawer, Button, Input, Select, Collapse, Switch, Radio, TimePicker,
  DatePicker, Empty, Spin, Tooltip, Badge, Checkbox, Tag, message, notification,
  Tabs, Avatar, Modal,
} from "antd";
import {
  CarOutlined, PlusOutlined, QuestionCircleOutlined,
  EditOutlined, DeleteOutlined, UserOutlined, CalendarOutlined,
  WechatOutlined, UploadOutlined, InboxOutlined,
} from "@ant-design/icons";
import { useJsApiLoader, Autocomplete } from "@react-google-maps/api";
import { API_DOMAIN } from "../constants";
import { AuthContext } from "../contexts/AuthContext";
import { GMAPS_KEY, GMAPS_LIBS } from "./constants";
import { parseAddresses, getAgentColor, agentInitials, _fetchSRMUsers } from "./helpers";
import { PIPELINE_COLUMNS, URGENCY_CFG } from "./pipeline";
import { WorkflowTaskDrawer } from "./WorkflowTaskDrawer";
import { _resolveMediaUrl } from "./MessageParts";
import { SendToSuppliersModal } from "./SendToSuppliersModal";

function GalleryPickerThumb({ item, selected, selectionIndex, onToggle, disabled, showSourceIcon = false }) {
  const [url, setUrl] = useState(item.source === "upload" ? item.previewUrl : null);
  useEffect(() => {
    if (item.source === "upload") return;
    if (!item?.data) return;
    _resolveMediaUrl(item.data).then(setUrl).catch(() => {});
  }, [item?.data, item?.source]);

  const isChat = item.source === "chat";

  return (
    <div
      onClick={() => (!disabled || selected) && onToggle()}
      style={{
        position: "relative", aspectRatio: "1", borderRadius: 6, overflow: "hidden",
        cursor: disabled && !selected ? "not-allowed" : "pointer",
        border: selected ? "3px solid #1677ff" : "2px solid transparent",
        background: "#f0f0f0", flexShrink: 0,
        transition: "border-color 0.15s",
        opacity: disabled && !selected ? 0.45 : 1,
      }}
    >
      {url
        ? <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
        : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}><Spin size="small" /></div>
      }
      {selected && (
        <div style={{
          position: "absolute", top: 4, right: 4,
          width: 20, height: 20, borderRadius: "50%",
          background: "#1677ff", color: "#fff",
          fontSize: 11, fontWeight: 700,
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
        }}>
          {selectionIndex}
        </div>
      )}
      {showSourceIcon && (
        <div style={{
          position: "absolute", bottom: 3, left: 3,
          background: "rgba(0,0,0,0.55)", borderRadius: 4,
          padding: "1px 4px", display: "flex", alignItems: "center",
        }}>
          {isChat
            ? <WechatOutlined style={{ fontSize: 10, color: "#fff" }} />
            : <UploadOutlined style={{ fontSize: 10, color: "#fff" }} />
          }
        </div>
      )}
    </div>
  );
}

const getStatusDisplay = (status) => {
  const col = PIPELINE_COLUMNS.find(c => !c.isChat && c.statuses?.includes(status));
  return col ? { label: col.label, color: col.color } : { label: status, color: "#8c8c8c" };
};

export function CustomerEditDrawer({ customer, open, onClose, onSaved, defaultTab = "customer", imageMessages = [], onQueryCreated }) {
  const { authState } = useContext(AuthContext);

  // customer data tab state
  const [draft, setDraft] = useState({});
  const [addresses, setAddresses] = useState([]);
  const [sourceOptions, setSourceOptions] = useState([]);
  const [subForm, setSubForm] = useState(null);
  const [vehicleSubForm, setVehicleSubForm] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [brandOptions, setBrandOptions] = useState([]);
  const [modelOptions, setModelOptions] = useState([]);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);
  const [vehiclesSaving, setVehiclesSaving] = useState(false);
  const [editingVehicleId, setEditingVehicleId] = useState(null);
  const [emailError, setEmailError] = useState("");
  const [activeKeys, setActiveKeys] = useState(["addresses"]);
  const [saving, setSaving] = useState(false);

  // tab state
  const [activeTab, setActiveTab] = useState(defaultTab);

  // queries tab state
  const [queriesList, setQueriesList] = useState([]);
  const [queriesLoading, setQueriesLoading] = useState(false);
  const [createFormOpen, setCreateFormOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ vehicle_id: null, urgency: 2, assigned_to: authState.id, remarks: "", parts: [], images: [] });
  const [savingDraft, setSavingDraft] = useState(false);
  const [savingQuery, setSavingQuery] = useState(false);
  const [sendQueryPk, setSendQueryPk] = useState(null);
  const [selectedQueryPk, setSelectedQueryPk] = useState(null);
  const [agentOptions, setAgentOptions] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [partsInfoOptions, setPartsInfoOptions] = useState([]);
  const [partsInfoLoading, setPartsInfoLoading] = useState(false);
  const [activePartSearch, setActivePartSearch] = useState("");
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryModalTab, setGalleryModalTab] = useState("chat");

  const autocompleteRef = useRef(null);
  const subFormRef = useRef(null);
  const vehicleSubFormRef = useRef(null);

  const { isLoaded: isMapsLoaded } = useJsApiLoader({
    googleMapsApiKey: GMAPS_KEY,
    libraries: GMAPS_LIBS,
  });

  useEffect(() => {
    if (!open || !customer) return;
    setActiveTab(defaultTab ?? "customer");
    setDraft({
      customer_name: customer.customer_name || "",
      customer_real_name: customer.customer_real_name || "",
      customer_email: customer.customer_email || "",
      source: customer.source ?? null,
      status: customer.status ? true : false,
      type: customer.type ?? null,
    });
    setAddresses(
      parseAddresses(customer.customer_addresses_new ?? customer.addresses)
        .map((a, i) => a.id != null ? a : { ...a, id: `existing-${i}` })
    );
    setSubForm(null);
    setVehicleSubForm(null);
    setEditingVehicleId(null);
    setModelOptions([]);
    setActiveKeys(["addresses"]);
    setCreateFormOpen(false);
    setCreateForm({ vehicle_id: null, urgency: 2, assigned_to: authState.id, remarks: "", parts: [], images: [] });
    loadVehicles(customer?.customer_id);
  }, [open, customer]); // eslint-disable-line

  useEffect(() => {
    if (sourceOptions.length > 0) return;
    axios.post(
      API_DOMAIN + "/whatsapp/get_misc_mode",
      { mode: "query_source" },
      { headers: { accessToken: localStorage.getItem("accessToken") } }
    ).then(res => {
      const list = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
      setSourceOptions(list);
    }).catch(() => { });
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!open || brandOptions.length > 0) return;
    setLoadingBrands(true);
    axios.post(
      API_DOMAIN + "/whatsapp/get_makes_v2",
      { name: "" },
      { headers: { accessToken: localStorage.getItem("accessToken") } }
    ).then(res => {
      if (res.data.success) {
        setBrandOptions(res.data.data.map(item => ({ value: item.id, label: item.name })));
      }
    }).catch(() => { }).finally(() => setLoadingBrands(false));
  }, [open]); // eslint-disable-line

  useEffect(() => {
    if (open && activeTab === "queries" && customer?.customer_id) {
      loadQueries();
    }
  }, [open, activeTab, customer?.customer_id]); // eslint-disable-line

  useEffect(() => {
    if (!createFormOpen) return;
    if (agentOptions.length === 0) {
      const selfName = authState.user?.name || authState.user?.username || "";
      if (selfName) setAgentOptions([{ id: authState.id, fullName: selfName }]);
      setAgentsLoading(true);
      _fetchSRMUsers().then(list => { setAgentOptions(list); setAgentsLoading(false); });
    }
    if (partsInfoOptions.length === 0) {
      setPartsInfoLoading(true);
      axios.post(`${API_DOMAIN}/whatsapp/get_parts_info`, {}, {
        headers: { accessToken: localStorage.getItem("accessToken") },
      }).then(res => {
        const list = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
        setPartsInfoOptions(list.map(p =>
          typeof p === "string" ? { value: p, label: p } : { value: p.value ?? p.name, label: p.label ?? p.name ?? p.value }
        ));
      }).catch(() => {}).finally(() => setPartsInfoLoading(false));
    }
  }, [createFormOpen]); // eslint-disable-line

  function processUploadedFiles(files) {
    files.slice(0, 10).forEach(file => {
      const reader = new FileReader();
      reader.onload = e => {
        setCreateForm(f => {
          if (f.images.length >= 10) return f;
          return {
            ...f,
            images: [...f.images, {
              id: `upload-${Date.now()}-${Math.random().toString(36).slice(2)}`,
              source: "upload",
              file,
              previewUrl: e.target.result,
              name: file.name,
            }],
          };
        });
      };
      reader.readAsDataURL(file);
    });
  }

  async function handleAddNewPartInfo(value, idx) {
    try {
      await axios.post(
        `${API_DOMAIN}/whatsapp/add_new_part_info`,
        { value },
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
      setPartsInfoOptions(prev => [...prev, { value, label: value }]);
    } catch { message.error("Failed to save new part name"); return; }
    setCreateForm(f => {
      const parts = [...f.parts];
      parts[idx] = { ...parts[idx], part_name: value };
      return { ...f, parts };
    });
    setActivePartSearch("");
  }

  async function loadVehicleModels(brandId) {
    if (!brandId) { setModelOptions([]); return; }
    setLoadingModels(true);
    try {
      const res = await axios.post(
        API_DOMAIN + "/whatsapp/get_models_v2",
        { make: brandId },
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
      if (res.data.success) {
        setModelOptions(res.data.data.map(item => ({ value: item.id, label: item.model })));
      }
    } catch { setModelOptions([]); }
    finally { setLoadingModels(false); }
  }

  async function loadVehicles(customerId) {
    if (!customerId) { setVehicles([]); return; }
    setVehiclesLoading(true);
    try {
      const res = await axios.get(
        `${API_DOMAIN}/customer_vehicles/get_by_customer?customer_id=${customerId}`,
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
      setVehicles(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch { setVehicles([]); }
    finally { setVehiclesLoading(false); }
  }

  async function loadQueries() {
    if (!customer?.customer_id) return;
    setQueriesLoading(true);
    try {
      const res = await axios.get(`${API_DOMAIN}/queryset_v2/get`, {
        params: { customer_id: customer.customer_id },
        headers: { accessToken: localStorage.getItem("accessToken") },
      });
      if (res.data.success) setQueriesList(res.data.data || []);
    } catch { setQueriesList([]); }
    finally { setQueriesLoading(false); }
  }

  async function handleCreateQuery(status = "draft") {
    if (!customer?.customer_id) return;
    const setSaving = status === "draft" ? setSavingDraft : setSavingQuery;
    setSaving(true);
    try {
      const headers = { accessToken: localStorage.getItem("accessToken") };

      // Upload all images to the server and collect permanent URLs
      // - device images: upload File object directly
      // - chat images: resolve WATI signed URL → fetch blob → upload
      let allImages = [];
      if (createForm.images.length > 0) {
        try {
          allImages = await Promise.all(
            createForm.images.map(async img => {
              const fd = new FormData();
              if (img.source === "upload") {
                fd.append("file", img.file);
              } else {
                // Use the existing media download proxy to avoid CORS, then re-upload
                const blobRes = await axios.post(
                  `${API_DOMAIN}/wati_messages/download_wati_media_file`,
                  { file_name: img.data },
                  { headers, responseType: "blob" }
                );
                const blob = blobRes.data;
                const ext = (blob.type || "image/jpeg").split("/")[1] || "jpg";
                fd.append("file", new File([blob], `chat_image_${Date.now()}.${ext}`, { type: blob.type }));
              }
              const res = await axios.post(`${API_DOMAIN}/whatsapp/image_upload`, fd, { headers });
              return res.data;
            })
          );
        } catch {
          message.error("Failed to upload one or more images. Please try again.");
          setSaving(false);
          return;
        }
      }

      const validParts = createForm.parts
        .filter(p => p.part_name?.trim())
        .map(p => ({
          part_name: p.part_name.trim(),
          quantity: p.quantity || 1,
          ...(p.side && { side: p.side }),
          ...(p.condition_preference && { condition_preference: p.condition_preference }),
          ...(p.part_number?.trim() && { part_number: p.part_number.trim() }),
        }));
      const payload = {
        customer_id: customer.customer_id,
        created_by: authState.id,
        status,
        ...(createForm.vehicle_id != null && { vehicle_id: createForm.vehicle_id }),
        ...(createForm.urgency != null && { urgency: createForm.urgency }),
        ...(createForm.assigned_to != null && { assigned_to: createForm.assigned_to }),
        ...(createForm.remarks?.trim() && { remarks: createForm.remarks.trim() }),
        ...(validParts.length > 0 && { parts: validParts }),
        ...(allImages.length > 0 && { images: allImages }),
      };
      const res = await axios.post(
        `${API_DOMAIN}/queryset_v2/create`,
        payload,
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
      if (!res.data.success) throw new Error(res.data.message);
      message.success("Query created");
      setCreateFormOpen(false);
      setCreateForm({ vehicle_id: null, urgency: 2, assigned_to: authState.id, remarks: "", parts: [], images: [] });

      // Inject card directly into the kanban board without a refetch
      const selectedVehicle = vehicles.find(v => v.id === createForm.vehicle_id) ?? null;
      const assignedAgent = agentOptions.find(a => a.id === createForm.assigned_to);
      const newCard = {
        id: res.data.data?.task_id,
        query_id: res.data.data?.query_id,
        status: res.data.data?.status ?? "draft",
        urgency: createForm.urgency,
        created_at: new Date().toISOString(),
        assigned_user: assignedAgent ? { name: assignedAgent.fullName } : null,
        vehicle: selectedVehicle ? {
          brand_name: selectedVehicle.brand_name || selectedVehicle.brand_label || selectedVehicle.brand || "",
          model_name: selectedVehicle.model_name || selectedVehicle.model_label || selectedVehicle.model || "",
          year: selectedVehicle.year || null,
        } : null,
        customer: {
          customer_name: customer.customer_real_name || customer.customer_name || null,
          customer_mobile: customer.customer_mobile || null,
        },
        query_parts: res.data.data?.query_parts || [],
        images: res.data.data?.images ?? allImages,
        remarks: res.data.data?.remarks ?? createForm.remarks ?? "",
        has_after_sales: false,
      };
      // Pass openSend=true when "Create Query" so the parent opens the send modal
      onQueryCreated?.(newCard, status === "waiting_for_price");

      await loadQueries();
    } catch (e) {
      message.error(e.message || "Failed to create query");
    } finally {
      setSaving(false);
    }
  }

  const setField = (key, val) => setDraft(d => ({ ...d, [key]: val }));

  const openAddSubForm = () => {
    setActiveKeys(["addresses"]);
    setSubForm({
      name: customer?.customer_real_name?.trim() || customer?.customer_name?.trim() || "",
      number: customer?.customer_mobile || "",
      address: "",
      type: "Home",
      pref_time: null,
    });
    setTimeout(() => subFormRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  const openAddVehicleForm = () => {
    setActiveKeys(["vehicles"]);
    setVehicleSubForm({
      brand_id: null, model_id: null, variant: "", year: null,
      vin: "", engine_code: "", engine_displacement: "", engine_layout: null,
      transmission_type: null, transmission_code: "", drivetrain: null,
      fuel_type: null, country_spec: null, trim_info: "", notes: "",
      is_primary: false, status: true,
    });
    setModelOptions([]);
    setTimeout(() => {
      const el = vehicleSubFormRef.current;
      if (!el) return;
      const container = el.closest(".ant-drawer-body");
      if (container) {
        const top = el.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop - 8;
        container.scrollTo({ top, behavior: "smooth" });
      } else {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 350);
  };

  const editAddress = (addr) => setSubForm({ ...addr });
  const deleteAddress = (id) => setAddresses(prev => prev.filter(a => a.id !== id));

  const saveSubForm = () => {
    if (!subForm?.name?.trim() || !subForm?.number?.trim() || !subForm?.address?.trim()) {
      message.warning("Name, number and address are required");
      return;
    }
    const isExisting = addresses.some(a => a.id === subForm.id);
    if (isExisting) {
      setAddresses(prev => prev.map(a => a.id === subForm.id ? subForm : a));
    } else {
      setAddresses(prev => [...prev, { ...subForm, id: Date.now() }]);
    }
    setSubForm(null);
  };

  const handleSave = async () => {
    if (!customer?.customer_id) return;
    if (emailError) { message.warning("Please fix the email address before saving"); return; }
    const payload = { customer_id: customer.customer_id };

    if (draft.customer_name !== (customer.customer_name || "")) payload.customer_name = draft.customer_name;
    if (draft.customer_real_name !== (customer.customer_real_name || "")) payload.customer_real_name = draft.customer_real_name;
    if (draft.customer_email !== (customer.customer_email || "")) payload.customer_email = draft.customer_email;
    if (draft.source !== (customer.source ?? null)) payload.source = draft.source;
    if ((draft.status ? 1 : 0) !== (customer.status ? 1 : 0)) payload.status = draft.status ? 1 : 0;
    if (draft.type !== (customer.type ?? null)) payload.type = draft.type;

    const origAddresses = parseAddresses(customer.customer_addresses_new ?? customer.addresses)
      .map((a, i) => a.id != null ? a : { ...a, id: `existing-${i}` });
    if (JSON.stringify(addresses) !== JSON.stringify(origAddresses)) {
      payload.customer_addresses_new = addresses;
    }

    if (Object.keys(payload).length === 1) {
      message.info("No changes to save");
      return;
    }

    setSaving(true);
    try {
      const res = await axios.post(
        API_DOMAIN + "/customers/update",
        payload,
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
      if (!res.data.success) throw new Error(res.data.message);
      notification.success({ message: "Customer updated", placement: "topRight", duration: 2 });
      onSaved({ ...(res.data.data || {}), type: draft.type });
      onClose();
    } catch (e) {
      message.error(e.message || "Failed to save customer");
    } finally {
      setSaving(false);
    }
  };

  const customerDataTab = (
    <>
      <div style={{ display: "grid", gap: 14 }}>
        <div>
          <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>Customer Name</div>
          <Input
            value={draft.customer_name || ""}
            onChange={e => setField("customer_name", e.target.value)}
            placeholder="Display name"
          />
        </div>
        <div>
          <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>Real Name</div>
          <Input
            value={draft.customer_real_name || ""}
            onChange={e => setField("customer_real_name", e.target.value)}
            placeholder="Legal / real name"
          />
        </div>
        <div>
          <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>Email</div>
          <Input
            value={draft.customer_email || ""}
            onChange={e => {
              const v = e.target.value;
              setField("customer_email", v);
              if (v && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) setEmailError("Invalid email address");
              else setEmailError("");
            }}
            placeholder="Email address"
            status={emailError ? "error" : ""}
          />
          {emailError && <div style={{ fontSize: 11, color: "#ff4d4f", marginTop: 2 }}>{emailError}</div>}
        </div>
        <div>
          <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>Phone</div>
          <div style={{ fontSize: 13, color: "#262626", padding: "4px 0", display: "flex", alignItems: "center", gap: 8 }}>
            <span>{customer?.customer_mobile || "—"}</span>
            {(customer?.country || customer?.customer_country) && (
              <span style={{ fontSize: 12, color: "#8c8c8c" }}>
                {customer?.country || customer?.customer_country}
              </span>
            )}
          </div>
        </div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 140 }}>
            <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>Source</div>
            <Select
              style={{ width: "100%" }}
              value={draft.source ?? null}
              onChange={val => setField("source", val ?? null)}
              allowClear
              placeholder="Select source"
              options={sourceOptions.map(o => ({ value: o.value, label: o.label }))}
            />
          </div>
          <div>
            <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>Type</div>
            <Select
              style={{ width: 90 }}
              value={draft.type ?? null}
              onChange={val => setField("type", val ?? null)}
              allowClear
              placeholder="Type?"
              options={[{ value: 0, label: "B2B" }, { value: 1, label: "B2C" }]}
            />
          </div>
          <div>
            <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>Active</div>
            <Switch
              checked={!!draft.status}
              onChange={val => setField("status", val)}
              checkedChildren="Active"
              unCheckedChildren="Off"
            />
          </div>
        </div>
      </div>

      <Collapse
        accordion
        style={{ marginTop: 20 }}
        activeKey={activeKeys[0] ?? null}
        onChange={key => setActiveKeys(key ? [key] : [])}
        items={[
          draft.type !== 0 && {
            key: "vehicles",
            label: (
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <CarOutlined />
                Vehicles
                {draft.type === 1 && vehicles.length > 0 && (
                  <Badge count={vehicles.length} size="small" color="#8c8c8c" />
                )}
                {draft.type === null && (
                  <Tag color="orange" style={{ fontSize: 10, margin: 0, padding: "0 5px" }}>
                    Set type first
                  </Tag>
                )}
              </span>
            ),
            extra: draft.type === 1 && (
              <Button size="small" icon={<PlusOutlined />}
                onClick={e => { e.stopPropagation(); openAddVehicleForm(); }}>
                Add
              </Button>
            ),
            children: draft.type === null ? (
              <div style={{ textAlign: "center", padding: "16px 0", color: "#8c8c8c" }}>
                <QuestionCircleOutlined style={{ fontSize: 22, color: "#d48806", marginBottom: 8, display: "block" }} />
                <div style={{ fontSize: 13, marginBottom: 4 }}>Customer type not set</div>
                <div style={{ fontSize: 12 }}>Set the customer type to <strong>B2C</strong> above to manage vehicles.</div>
              </div>
            ) : (
              <>
                {vehicleSubForm !== null && (
                  <div ref={vehicleSubFormRef}
                    style={{ border: `1px solid ${editingVehicleId ? "#d46b08" : "#1677ff"}`, borderRadius: 6, padding: 12, background: editingVehicleId ? "#fff7e6" : "#f0f5ff", marginBottom: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: editingVehicleId ? "#d46b08" : "#1677ff", marginBottom: 8 }}>
                      {editingVehicleId ? "Edit Vehicle" : "Add New Vehicle"}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 12px" }}>
                      <div>
                        <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 3 }}>Brand</div>
                        <Select
                          style={{ width: "100%" }}
                          showSearch
                          loading={loadingBrands}
                          placeholder="Select brand"
                          filterOption={(input, opt) => opt.label?.toLowerCase().includes(input.toLowerCase())}
                          options={brandOptions}
                          value={vehicleSubForm.brand_id ?? null}
                          onChange={val => {
                            setVehicleSubForm(f => ({ ...f, brand_id: val ?? null, model_id: null }));
                            loadVehicleModels(val);
                          }}
                          allowClear
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 3 }}>Model</div>
                        <Select
                          style={{ width: "100%" }}
                          showSearch
                          loading={loadingModels}
                          placeholder={vehicleSubForm.brand_id ? "Select model" : "Select brand first"}
                          disabled={!vehicleSubForm.brand_id}
                          filterOption={(input, opt) => opt.label?.toLowerCase().includes(input.toLowerCase())}
                          options={modelOptions}
                          value={vehicleSubForm.model_id ?? null}
                          onChange={val => setVehicleSubForm(f => ({ ...f, model_id: val ?? null }))}
                          allowClear
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 3 }}>Variant</div>
                        <Input
                          placeholder="e.g. Sport, LX, SE"
                          value={vehicleSubForm.variant}
                          onChange={e => setVehicleSubForm(f => ({ ...f, variant: e.target.value }))}
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 3 }}>Year</div>
                        <DatePicker
                          style={{ width: "100%" }}
                          picker="year"
                          placeholder="Select year"
                          value={vehicleSubForm.year ? dayjs(vehicleSubForm.year, "YYYY") : null}
                          onChange={(_, str) => setVehicleSubForm(f => ({ ...f, year: str || null }))}
                          disabledDate={d => d && (d.year() < 2000 || d.year() > dayjs().year())}
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 3 }}>VIN / Chassis No.</div>
                        <Input
                          placeholder="e.g. 1HGCM82633A004352"
                          value={vehicleSubForm.vin}
                          onChange={e => setVehicleSubForm(f => ({ ...f, vin: e.target.value }))}
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 3 }}>Engine Code</div>
                        <Input
                          placeholder="e.g. 2AR-FE"
                          value={vehicleSubForm.engine_code}
                          onChange={e => setVehicleSubForm(f => ({ ...f, engine_code: e.target.value }))}
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 3 }}>Engine Displacement (L)</div>
                        <Input
                          placeholder="e.g. 2.0"
                          value={vehicleSubForm.engine_displacement}
                          onChange={e => {
                            const val = e.target.value;
                            if (val === "" || /^\d{0,2}(\.\d{0,1})?$/.test(val)) {
                              setVehicleSubForm(f => ({ ...f, engine_displacement: val }));
                            }
                          }}
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 3 }}>Engine Layout</div>
                        <Select
                          style={{ width: "100%" }}
                          placeholder="Select layout"
                          allowClear
                          value={vehicleSubForm.engine_layout ?? null}
                          onChange={val => setVehicleSubForm(f => ({ ...f, engine_layout: val ?? null }))}
                          options={[
                            { value: "inline-3", label: "Inline-3" },
                            { value: "inline-4", label: "Inline-4" },
                            { value: "inline-6", label: "Inline-6" },
                            { value: "v6", label: "V6" },
                            { value: "v8", label: "V8" },
                            { value: "v10", label: "V10" },
                            { value: "v12", label: "V12" },
                            { value: "flat-4", label: "Flat-4" },
                            { value: "flat-6", label: "Flat-6" },
                            { value: "w12", label: "W12" },
                            { value: "w16", label: "W16" },
                            { value: "rotary", label: "Rotary" },
                            { value: "electric", label: "Electric Motor" },
                          ]}
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 3 }}>Transmission Type</div>
                        <Select
                          style={{ width: "100%" }}
                          placeholder="Select type"
                          allowClear
                          value={vehicleSubForm.transmission_type ?? null}
                          onChange={val => setVehicleSubForm(f => ({ ...f, transmission_type: val ?? null }))}
                          options={[
                            { value: "automatic", label: "Automatic" },
                            { value: "manual", label: "Manual" },
                            { value: "cvt", label: "CVT" },
                            { value: "dct", label: "DCT (Dual Clutch)" },
                            { value: "amt", label: "AMT" },
                            { value: "semi-automatic", label: "Semi-Automatic" },
                          ]}
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 3 }}>Transmission Code</div>
                        <Input
                          placeholder="e.g. A750E"
                          value={vehicleSubForm.transmission_code}
                          onChange={e => setVehicleSubForm(f => ({ ...f, transmission_code: e.target.value }))}
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 3 }}>Drivetrain</div>
                        <Select
                          style={{ width: "100%" }}
                          placeholder="Select drivetrain"
                          allowClear
                          value={vehicleSubForm.drivetrain ?? null}
                          onChange={val => setVehicleSubForm(f => ({ ...f, drivetrain: val ?? null }))}
                          options={[
                            { value: "fwd", label: "FWD" },
                            { value: "rwd", label: "RWD" },
                            { value: "awd", label: "AWD" },
                            { value: "4wd", label: "4WD" },
                          ]}
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 3 }}>Fuel Type</div>
                        <Select
                          style={{ width: "100%" }}
                          placeholder="Select fuel type"
                          allowClear
                          value={vehicleSubForm.fuel_type ?? null}
                          onChange={val => setVehicleSubForm(f => ({ ...f, fuel_type: val ?? null }))}
                          options={[
                            { value: "petrol", label: "Petrol" },
                            { value: "diesel", label: "Diesel" },
                            { value: "hybrid", label: "Hybrid" },
                            { value: "phev", label: "Plug-in Hybrid" },
                            { value: "electric", label: "Electric" },
                            { value: "lpg", label: "LPG" },
                            { value: "cng", label: "CNG" },
                            { value: "hydrogen", label: "Hydrogen" },
                          ]}
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 3 }}>Country Spec</div>
                        <Select
                          style={{ width: "100%" }}
                          placeholder="Select spec"
                          allowClear
                          value={vehicleSubForm.country_spec ?? null}
                          onChange={val => setVehicleSubForm(f => ({ ...f, country_spec: val ?? null }))}
                          options={[
                            { value: "gcc", label: "GCC" },
                            { value: "us", label: "US" },
                            { value: "uk", label: "UK" },
                            { value: "eu", label: "EU" },
                            { value: "japan", label: "Japan" },
                            { value: "canada", label: "Canada" },
                            { value: "australia", label: "Australia" },
                            { value: "china", label: "China" },
                            { value: "other", label: "Other" },
                          ]}
                        />
                      </div>
                      <div style={{ gridColumn: "1 / -1" }}>
                        <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 3 }}>Trim Info</div>
                        <Input
                          placeholder="e.g. Sport Edition, Night Package"
                          value={vehicleSubForm.trim_info}
                          onChange={e => setVehicleSubForm(f => ({ ...f, trim_info: e.target.value }))}
                        />
                      </div>
                      <div style={{ gridColumn: "1 / -1" }}>
                        <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 3 }}>Additional Notes</div>
                        <Input.TextArea
                          rows={3}
                          placeholder="Any extra details about the vehicle..."
                          value={vehicleSubForm.notes}
                          onChange={e => setVehicleSubForm(f => ({ ...f, notes: e.target.value }))}
                        />
                      </div>
                      <div style={{ gridColumn: "1 / -1", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Checkbox
                          checked={vehicleSubForm.is_primary}
                          onChange={e => setVehicleSubForm(f => ({ ...f, is_primary: e.target.checked }))}
                        >
                          <span style={{ fontSize: 12 }}>Primary vehicle</span>
                        </Checkbox>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontSize: 12, color: "#8c8c8c" }}>Active</span>
                          <Switch
                            size="small"
                            checked={vehicleSubForm.status}
                            onChange={val => setVehicleSubForm(f => ({ ...f, status: val }))}
                          />
                        </div>
                      </div>
                    </div>
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 10 }}>
                      <Button size="small" onClick={() => { setVehicleSubForm(null); setEditingVehicleId(null); setModelOptions([]); }}>Cancel</Button>
                      <Button size="small" type="primary" loading={vehiclesSaving}
                        style={editingVehicleId ? { background: "#d46b08", borderColor: "#d46b08" } : {}}
                        onClick={async () => {
                          if (!vehicleSubForm.brand_id) { message.warning("Please select a brand"); return; }
                          if (!vehicleSubForm.model_id) { message.warning("Please select a model"); return; }
                          setVehiclesSaving(true);
                          const isEdit = !!editingVehicleId;
                          try {
                            const vf = vehicleSubForm;
                            const payload = isEdit
                              ? { id: editingVehicleId }
                              : { customer_id: customer.customer_id, brand: vf.brand_id, model: vf.model_id, created_by: authState.id };
                            if (!isEdit || true) {
                              payload.brand = vf.brand_id;
                              payload.model = vf.model_id;
                              payload.is_primary_vehicle = vf.is_primary ? 1 : 0;
                              payload.status = vf.status ? 1 : 0;
                              payload.variant = vf.variant || null;
                              payload.year = vf.year || null;
                              payload.vin = vf.vin || null;
                              payload.engine_code = vf.engine_code || null;
                              payload.engine_displacement = vf.engine_displacement ? parseFloat(vf.engine_displacement) : null;
                              payload.engine_layout = vf.engine_layout || null;
                              payload.transmission_type = vf.transmission_type || null;
                              payload.transmission_code = vf.transmission_code || null;
                              payload.drivetrain = vf.drivetrain || null;
                              payload.fuel_type = vf.fuel_type || null;
                              payload.country_spec = vf.country_spec || null;
                              payload.trim_info = vf.trim_info || null;
                              payload.notes = vf.notes || null;
                            }
                            const endpoint = isEdit ? "/customer_vehicles/update" : "/customer_vehicles/create";
                            const res = await axios.post(
                              API_DOMAIN + endpoint,
                              payload,
                              { headers: { accessToken: localStorage.getItem("accessToken") } }
                            );
                            if (!res.data.success) throw new Error(res.data.message);
                            message.success(isEdit ? "Vehicle updated" : "Vehicle added");
                            setVehicleSubForm(null);
                            setEditingVehicleId(null);
                            setModelOptions([]);
                            await loadVehicles(customer.customer_id);
                          } catch (e) {
                            message.error(e.message || (isEdit ? "Failed to update vehicle" : "Failed to add vehicle"));
                          } finally {
                            setVehiclesSaving(false);
                          }
                        }}>
                        {editingVehicleId ? "Save Changes" : "Add Vehicle"}
                      </Button>
                    </div>
                  </div>
                )}
                {vehiclesLoading
                  ? <div style={{ textAlign: "center", padding: "16px 0" }}><Spin size="small" /></div>
                  : vehicles.length === 0
                    ? <Empty description="No vehicles yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    : vehicles.map(v => {
                      const brandLabel = v.brand_name || v.brand_label || (brandOptions.find(b => b.value === v.brand)?.label) || v.brand || "—";
                      const modelLabel = v.model_name || v.model_label || v.model || "—";
                      const isPrimary = v.is_primary_vehicle === 1 || v.is_primary === true;
                      const isActive = v.status !== 0 && v.status !== false;
                      return (
                        <div key={v.id}
                          style={{
                            border: "1px solid #d9d9d9", borderRadius: 6, padding: "8px 10px",
                            marginBottom: 6, display: "flex", justifyContent: "space-between", alignItems: "flex-start"
                          }}>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 13 }}>
                              {brandLabel} {modelLabel} {v.year ? `(${v.year})` : ""}
                              {isPrimary && <Tag color="blue" style={{ marginLeft: 6, fontSize: 10 }}>Primary</Tag>}
                              {!isActive && <Tag color="default" style={{ marginLeft: 4, fontSize: 10 }}>Inactive</Tag>}
                            </div>
                            {v.variant && <div style={{ fontSize: 11, color: "#8c8c8c" }}>Variant: {v.variant}</div>}
                            {v.vin && <div style={{ fontSize: 11, color: "#8c8c8c" }}>VIN: {v.vin}</div>}
                            {v.engine_displacement && <div style={{ fontSize: 11, color: "#8c8c8c" }}>{v.engine_displacement}L{v.engine_layout ? ` · ${v.engine_layout}` : ""}{v.fuel_type ? ` · ${v.fuel_type}` : ""}</div>}
                          </div>
                          <div style={{ display: "flex", gap: 2 }}>
                            <Tooltip title="Edit vehicle">
                              <Button type="text" size="small" icon={<EditOutlined />}
                                style={{ color: "#1677ff" }}
                                onClick={() => {
                                  setActiveKeys(["vehicles"]);
                                  setEditingVehicleId(v.id);
                                  setVehicleSubForm({
                                    brand_id: v.brand ?? null,
                                    model_id: v.model ?? null,
                                    variant: v.variant || "",
                                    year: v.year ? String(v.year).slice(0, 4) : null,
                                    vin: v.vin || "",
                                    engine_code: v.engine_code || "",
                                    engine_displacement: v.engine_displacement != null ? String(v.engine_displacement) : "",
                                    engine_layout: v.engine_layout || null,
                                    transmission_type: v.transmission_type || null,
                                    transmission_code: v.transmission_code || "",
                                    drivetrain: v.drivetrain || null,
                                    fuel_type: v.fuel_type || null,
                                    country_spec: v.country_spec || null,
                                    trim_info: v.trim_info || "",
                                    notes: v.notes || "",
                                    is_primary: v.is_primary_vehicle === 1,
                                    status: v.status !== 0,
                                  });
                                  if (v.brand) loadVehicleModels(v.brand);
                                  setTimeout(() => vehicleSubFormRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
                                }}
                              />
                            </Tooltip>
                            <Button type="text" size="small" danger icon={<DeleteOutlined />}
                              onClick={async () => {
                                try {
                                  const res = await axios.post(
                                    API_DOMAIN + "/customer_vehicles/delete",
                                    { id: v.id },
                                    { headers: { accessToken: localStorage.getItem("accessToken") } }
                                  );
                                  if (!res.data.success) throw new Error(res.data.message);
                                  setVehicles(prev => prev.filter(x => x.id !== v.id));
                                } catch (e) {
                                  message.error(e.message || "Failed to delete vehicle");
                                }
                              }} />
                          </div>
                        </div>
                      );
                    })
                }
              </>
            ),
          },

          {
            key: "addresses",
            label: (
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                Addresses
                {addresses.length > 0 && (
                  <Badge count={addresses.length} size="small" color="#8c8c8c" />
                )}
              </span>
            ),
            extra: (
              <Button size="small" icon={<PlusOutlined />}
                onClick={e => { e.stopPropagation(); openAddSubForm(); }}>
                Add
              </Button>
            ),
            children: (
              <>
                {subForm !== null && (
                  <div ref={subFormRef}
                    style={{
                      border: "1px solid #1677ff", borderRadius: 6, padding: 12, background: "#f0f5ff",
                      display: "grid", gap: 8, marginBottom: 12
                    }}>
                    <div style={{ display: "flex", gap: 8 }}>
                      <Input placeholder="Name *" value={subForm.name || ""}
                        onChange={e => setSubForm(f => ({ ...f, name: e.target.value }))} />
                      <Input placeholder="Number *" value={subForm.number || ""}
                        onChange={e => setSubForm(f => ({ ...f, number: e.target.value.replace(/\D/g, "") }))} />
                    </div>
                    {isMapsLoaded ? (
                      <Autocomplete
                        onLoad={ac => { autocompleteRef.current = ac; }}
                        onPlaceChanged={() => {
                          const place = autocompleteRef.current?.getPlace();
                          if (place?.formatted_address)
                            setSubForm(f => ({ ...f, address: place.formatted_address }));
                        }}
                      >
                        <Input placeholder="Address *" value={subForm.address || ""}
                          onChange={e => setSubForm(f => ({ ...f, address: e.target.value }))} />
                      </Autocomplete>
                    ) : (
                      <Input placeholder="Address *" value={subForm.address || ""}
                        onChange={e => setSubForm(f => ({ ...f, address: e.target.value }))} />
                    )}
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <Radio.Group value={subForm.type || "Home"}
                        onChange={e => setSubForm(f => ({ ...f, type: e.target.value }))}
                        options={[{ label: "Home", value: "Home" }, { label: "Office", value: "Office" }]}
                        optionType="button" buttonStyle="solid" size="small" />
                      <TimePicker.RangePicker format="hh:mm A" use12Hours allowClear size="small"
                        value={subForm.pref_time?.length === 2
                          ? [dayjs(subForm.pref_time[0]), dayjs(subForm.pref_time[1])]
                          : null}
                        onChange={val => setSubForm(f => ({
                          ...f, pref_time: val ? [val[0].toISOString(), val[1].toISOString()] : null,
                        }))} />
                    </div>
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                      <Button size="small" onClick={() => setSubForm(null)}>Cancel</Button>
                      <Button size="small" type="primary" onClick={saveSubForm}>Save Address</Button>
                    </div>
                  </div>
                )}

                <div style={{ display: "grid", gap: 8 }}>
                  {addresses.length === 0 && (
                    <Empty description="No addresses" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  )}
                  {addresses.map(addr => (
                    <div key={addr.id}
                      style={{ border: "1px solid #d9d9d9", borderRadius: 6, padding: "8px 12px", background: "#fafafa" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <span style={{ fontSize: 11, background: "#f0f0f0", color: "#595959", padding: "1px 6px", borderRadius: 4, fontWeight: 600 }}>
                          {addr.type}
                        </span>
                        <div style={{ display: "flex", gap: 4 }}>
                          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => editAddress(addr)} />
                          <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => deleteAddress(addr.id)} />
                        </div>
                      </div>
                      <div style={{ fontWeight: 600, marginTop: 4 }}>
                        {addr.name}
                        <span style={{ fontWeight: 400, color: "#8c8c8c", fontSize: 12, marginLeft: 8 }}>{addr.number}</span>
                      </div>
                      <div style={{ fontSize: 12, color: "#595959", marginTop: 2 }}>{addr.address}</div>
                      {addr.pref_time && Array.isArray(addr.pref_time) && addr.pref_time.length === 2 && (
                        <div style={{ fontSize: 11, color: "#8c8c8c", marginTop: 2 }}>
                          {dayjs(addr.pref_time[0]).format("hh:mm A")} – {dayjs(addr.pref_time[1]).format("hh:mm A")}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            ),
          },
        ].filter(Boolean)}
      />
    </>
  );

  const vehicleOptions = vehicles.map(v => {
    const brand = v.brand_name || v.brand_label || v.brand || "";
    const model = v.model_name || v.model_label || v.model || "";
    const year = v.year ? ` (${v.year})` : "";
    const spec = v.country_spec ? ` (${v.country_spec.toUpperCase()})` : "";
    return { value: v.id, label: `${brand} ${model}${year}${spec}`.trim() };
  });

  const queriesTab = (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Create form toggle */}
      {!createFormOpen ? (
        <Button
          type="dashed"
          icon={<PlusOutlined />}
          onClick={() => setCreateFormOpen(true)}
          block
        >
          New Query
        </Button>
      ) : (
        <div style={{
          border: "1px solid #1677ff", borderRadius: 8,
          padding: 14, background: "#f0f5ff",
          display: "flex", flexDirection: "column", gap: 12,
        }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: "#1677ff" }}>New Query</div>

          {/* Vehicle */}
          <div>
            <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>Vehicle</div>
            {vehicles.length === 0 ? (
              <div style={{ fontSize: 12, color: "#d48806", display: "flex", alignItems: "center", gap: 6 }}>
                <CarOutlined />
                No vehicles on record —{" "}
                <span
                  style={{ color: "#1677ff", cursor: "pointer", textDecoration: "underline" }}
                  onClick={() => { setActiveTab("customer"); openAddVehicleForm(); }}
                >
                  add one in Customer Data
                </span>
              </div>
            ) : (
              <Select
                style={{ width: "100%" }}
                placeholder="Select vehicle"
                allowClear
                value={createForm.vehicle_id ?? null}
                onChange={val => setCreateForm(f => ({ ...f, vehicle_id: val ?? null }))}
                options={vehicleOptions}
              />
            )}
          </div>

          {/* Urgency + Assigned To */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>Urgency</div>
              <Select
                style={{ width: "100%" }}
                value={createForm.urgency}
                onChange={val => setCreateForm(f => ({ ...f, urgency: val }))}
                options={Object.entries(URGENCY_CFG).map(([k, v]) => ({
                  value: Number(k),
                  label: v.label,
                }))}
              />
            </div>
            <div>
              <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>Assign To</div>
              {authState.user?.admin ? (
                <Select
                  style={{ width: "100%" }}
                  placeholder="Select agent"
                  loading={agentsLoading}
                  value={createForm.assigned_to ?? null}
                  onChange={val => setCreateForm(f => ({ ...f, assigned_to: val ?? authState.id }))}
                  options={agentOptions.map(a => ({ value: a.id, label: a.fullName }))}
                  optionRender={option => {
                    const name = String(option.label ?? option.value ?? "");
                    return (
                      <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <Avatar size={14} style={{ backgroundColor: getAgentColor(name), fontSize: 8, flexShrink: 0 }}>
                          {agentInitials(name)}
                        </Avatar>
                        {name}
                      </span>
                    );
                  }}
                />
              ) : (() => {
                const me = agentOptions.find(a => a.id === authState.id);
                const name = me?.fullName || authState.user?.name || authState.user?.username || "You";
                return (
                  <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px", border: "1px solid #d9d9d9", borderRadius: 6, background: "#fafafa" }}>
                    <Avatar size={16} style={{ backgroundColor: getAgentColor(name), fontSize: 9, flexShrink: 0 }}>
                      {agentInitials(name)}
                    </Avatar>
                    <span style={{ fontSize: 12, fontWeight: 600 }}>{name}</span>
                    <span style={{ fontSize: 10, color: "#8c8c8c", marginLeft: "auto" }}>You</span>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Remarks */}
          <div>
            <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>Remarks</div>
            <Input.TextArea
              rows={2}
              placeholder="Notes about this query..."
              value={createForm.remarks}
              onChange={e => setCreateForm(f => ({ ...f, remarks: e.target.value }))}
            />
          </div>

          {/* Parts */}
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
              <div style={{ fontSize: 12, color: "#8c8c8c" }}>Parts</div>
              <Button
                size="small"
                icon={<PlusOutlined />}
                onClick={() => setCreateForm(f => ({
                  ...f,
                  parts: [...f.parts, { part_name: "", quantity: 1, side: null, condition_preference: "used", part_number: "" }],
                }))}
              >
                Add Part
              </Button>
            </div>
            {createForm.parts.length === 0 ? (
              <div style={{ fontSize: 12, color: "#bfbfbf", textAlign: "center", padding: "6px 0" }}>
                No parts added yet
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {createForm.parts.map((part, idx) => (
                  <div key={idx} style={{
                    border: "1px solid #d0e4ff", borderRadius: 6,
                    padding: "8px 10px", background: "#fff",
                    display: "flex", flexDirection: "column", gap: 6,
                  }}>
                    {/* Row 1: name + qty + delete */}
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <Select
                        showSearch
                        size="small"
                        style={{ flex: 1 }}
                        placeholder="Part name *"
                        loading={partsInfoLoading}
                        value={part.part_name || undefined}
                        onSearch={setActivePartSearch}
                        onChange={(val, opt) => {
                          setActivePartSearch("");
                          setCreateForm(f => {
                            const parts = [...f.parts];
                            parts[idx] = { ...parts[idx], part_name: String(opt?.label ?? val ?? "") };
                            return { ...f, parts };
                          });
                        }}
                        filterOption={(input, opt) =>
                          String(opt.label ?? opt.value ?? "").toLowerCase().includes(input.toLowerCase())
                        }
                        options={partsInfoOptions}
                        dropdownRender={menu => (
                          <>
                            {menu}
                            {activePartSearch &&
                              !partsInfoOptions.some(o =>
                                String(o.label ?? o.value ?? "").toLowerCase() === activePartSearch.toLowerCase()
                              ) && (
                              <div
                                style={{ padding: "6px 12px", cursor: "pointer", color: "#1677ff", fontSize: 12, borderTop: "1px solid #f0f0f0" }}
                                onMouseDown={e => { e.preventDefault(); handleAddNewPartInfo(activePartSearch, idx); }}
                              >
                                + Add "{activePartSearch}"
                              </div>
                            )}
                          </>
                        )}
                      />
                      <Input
                        type="number"
                        min={1}
                        value={part.quantity}
                        onChange={e => setCreateForm(f => {
                          const parts = [...f.parts];
                          parts[idx] = { ...parts[idx], quantity: Math.max(1, parseInt(e.target.value) || 1) };
                          return { ...f, parts };
                        })}
                        style={{ width: 56 }}
                        size="small"
                        prefix={<span style={{ fontSize: 10, color: "#8c8c8c" }}>×</span>}
                      />
                      <Button
                        type="text" danger size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => setCreateForm(f => ({
                          ...f,
                          parts: f.parts.filter((_, i) => i !== idx),
                        }))}
                      />
                    </div>
                    {/* Row 2: side + condition + part number */}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
                      <Select
                        size="small"
                        placeholder="Side"
                        allowClear
                        value={part.side ?? null}
                        onChange={val => setCreateForm(f => {
                          const parts = [...f.parts];
                          parts[idx] = { ...parts[idx], side: val ?? null };
                          return { ...f, parts };
                        })}
                        options={[
                          { value: "left", label: "Left" },
                          { value: "right", label: "Right" },
                          { value: "both", label: "Both" },
                          { value: "front", label: "Front" },
                          { value: "rear", label: "Rear" },
                          { value: "front_left", label: "Front Left" },
                          { value: "front_right", label: "Front Right" },
                          { value: "rear_left", label: "Rear Left" },
                          { value: "rear_right", label: "Rear Right" },
                        ]}
                      />
                      <Select
                        size="small"
                        placeholder="Condition"
                        allowClear
                        value={part.condition_preference ?? null}
                        onChange={val => setCreateForm(f => {
                          const parts = [...f.parts];
                          parts[idx] = { ...parts[idx], condition_preference: val ?? null };
                          return { ...f, parts };
                        })}
                        options={[
                          { value: "new", label: "New" },
                          { value: "used", label: "Used" },
                          { value: "refurbished", label: "Refurbished" },
                        ]}
                      />
                      <Input
                        size="small"
                        placeholder="Part #"
                        value={part.part_number}
                        onChange={e => setCreateForm(f => {
                          const parts = [...f.parts];
                          parts[idx] = { ...parts[idx], part_number: e.target.value };
                          return { ...f, parts };
                        })}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Images */}
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
              <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                Images
                {createForm.images.length > 0 && (
                  <span style={{ marginLeft: 6, color: "#1677ff", fontWeight: 600 }}>
                    {createForm.images.length} / 10
                  </span>
                )}
              </div>
              <Button size="small" onClick={() => { setGalleryModalTab("chat"); setGalleryOpen(true); }}>
                Add Images
              </Button>
            </div>
            {createForm.images.length > 0 && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 4 }}>
                {createForm.images.map((img, i) => (
                  <GalleryPickerThumb
                    key={img.id}
                    item={img}
                    selected
                    selectionIndex={i + 1}
                    showSourceIcon
                    disabled={false}
                    onToggle={() => setCreateForm(f => ({ ...f, images: f.images.filter(x => x.id !== img.id) }))}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Action buttons */}
          {(() => {
            const hasVehicle = createForm.vehicle_id != null;
            const hasparts = createForm.parts.some(p => p.part_name?.trim());
            const hasImages = createForm.images.length > 0;
            const canCreate = hasVehicle && hasparts && hasImages;
            return (
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, paddingTop: 2 }}>
                <Button size="small" disabled={savingDraft || savingQuery} onClick={() => {
                  setCreateFormOpen(false);
                  setCreateForm({ vehicle_id: null, urgency: 2, assigned_to: authState.id, remarks: "", parts: [], images: [] });
                }}>
                  Cancel
                </Button>
                <Button size="small" loading={savingDraft} disabled={savingQuery} onClick={() => handleCreateQuery("draft")}>
                  Save Draft
                </Button>
                <Tooltip title={!canCreate ? "Add vehicle, at least one part, and at least one image to create a query" : ""}>
                  <Button
                    size="small"
                    type="primary"
                    disabled={!canCreate || savingDraft}
                    loading={savingQuery}
                    onClick={() => handleCreateQuery("waiting_for_price")}
                  >
                    Create Query
                  </Button>
                </Tooltip>
              </div>
            );
          })()}

          {/* Image picker modal */}
          <Modal
            open={galleryOpen}
            onCancel={() => setGalleryOpen(false)}
            footer={
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ fontSize: 12, color: "#8c8c8c" }}>{createForm.images.length} / 10 selected</span>
                <Button type="primary" onClick={() => setGalleryOpen(false)}>Done</Button>
              </div>
            }
            title="Add Images"
            width={500}
            styles={{ body: { padding: 0 } }}
            destroyOnClose={false}
          >
            <Tabs
              activeKey={galleryModalTab}
              onChange={setGalleryModalTab}
              size="small"
              tabBarStyle={{ margin: 0, paddingLeft: 16, paddingRight: 16 }}
              items={[
                {
                  key: "chat",
                  label: `From Chat${imageMessages.length ? ` (${imageMessages.length})` : ""}`,
                  children: (
                    <div style={{ padding: "12px 16px", maxHeight: 380, overflowY: "auto" }}>
                      {imageMessages.length === 0 ? (
                        <Empty description="No images in this chat" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ margin: "24px 0" }} />
                      ) : (
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6 }}>
                          {imageMessages.map(img => {
                            const enriched = { ...img, source: "chat" };
                            const isSelected = createForm.images.some(x => x.id === img.id);
                            const selectionIndex = createForm.images.findIndex(x => x.id === img.id) + 1;
                            const atLimit = createForm.images.length >= 10;
                            return (
                              <GalleryPickerThumb
                                key={img.id}
                                item={enriched}
                                selected={isSelected}
                                selectionIndex={isSelected ? selectionIndex : 0}
                                disabled={atLimit && !isSelected}
                                onToggle={() => setCreateForm(f => {
                                  if (isSelected) return { ...f, images: f.images.filter(x => x.id !== img.id) };
                                  if (f.images.length >= 10) return f;
                                  return { ...f, images: [...f.images, enriched] };
                                })}
                              />
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ),
                },
                {
                  key: "upload",
                  label: "Upload from Device",
                  children: (
                    <div style={{ padding: "12px 16px" }}>
                      <label style={{
                        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                        border: "1px dashed #d9d9d9", borderRadius: 8, padding: "24px 16px",
                        cursor: createForm.images.length >= 10 ? "not-allowed" : "pointer",
                        background: "#fafafa", marginBottom: 12,
                        opacity: createForm.images.length >= 10 ? 0.5 : 1,
                        transition: "border-color 0.2s",
                      }}
                        onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = "#1677ff"; }}
                        onDragLeave={e => { e.currentTarget.style.borderColor = "#d9d9d9"; }}
                        onDrop={e => {
                          e.preventDefault();
                          e.currentTarget.style.borderColor = "#d9d9d9";
                          if (createForm.images.length >= 10) return;
                          const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith("image/"));
                          processUploadedFiles(files);
                        }}
                      >
                        <input
                          type="file" multiple accept="image/*" style={{ display: "none" }}
                          disabled={createForm.images.length >= 10}
                          onChange={e => { processUploadedFiles(Array.from(e.target.files)); e.target.value = ""; }}
                        />
                        <InboxOutlined style={{ fontSize: 32, color: "#8c8c8c", marginBottom: 8 }} />
                        <div style={{ fontSize: 13, color: "#595959", fontWeight: 500 }}>Click or drag images here</div>
                        <div style={{ fontSize: 11, color: "#bfbfbf", marginTop: 4 }}>
                          {createForm.images.length >= 10 ? "Limit reached (10)" : `Up to ${10 - createForm.images.length} more`}
                        </div>
                      </label>
                      {createForm.images.filter(i => i.source === "upload").length > 0 && (
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6 }}>
                          {createForm.images.filter(i => i.source === "upload").map((img, i) => (
                            <GalleryPickerThumb
                              key={img.id}
                              item={img}
                              selected
                              selectionIndex={createForm.images.findIndex(x => x.id === img.id) + 1}
                              disabled={false}
                              onToggle={() => setCreateForm(f => ({ ...f, images: f.images.filter(x => x.id !== img.id) }))}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  ),
                },
              ]}
            />
          </Modal>

          <SendToSuppliersModal
            queryPk={sendQueryPk}
            open={!!sendQueryPk}
            onClose={() => setSendQueryPk(null)}
            onSent={() => setSendQueryPk(null)}
          />
        </div>
      )}

      {/* Query list */}
      {queriesLoading ? (
        <div style={{ textAlign: "center", padding: "32px 0" }}><Spin /></div>
      ) : queriesList.length === 0 ? (
        <Empty description="No queries yet" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ marginTop: 24 }} />
      ) : (
        queriesList.map(q => {
          const st = getStatusDisplay(q.status);
          const v = q.vehicle;
          const vehicleLabel = v
            ? [v.brand_name, v.model_name, v.year].filter(Boolean).join(" ")
            : null;
          const urgCfg = q.urgency ? URGENCY_CFG[q.urgency] : null;
          const assignee = q.assigned_user;
          const assigneeName = assignee
            ? (assignee.name ? `${assignee.name}${assignee.surname ? " " + assignee.surname : ""}` : assignee.username)
            : null;

          return (
            <div
              key={q.query_pk}
              onClick={() => setSelectedQueryPk(q.query_pk)}
              style={{
                border: "1px solid #e8e8e8",
                borderLeft: `4px solid ${st.color}`,
                borderRadius: 6,
                padding: "10px 12px",
                cursor: "pointer",
                background: "#fff",
                transition: "box-shadow 0.15s",
              }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.10)"}
              onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
                <span style={{ fontWeight: 700, color: "#1677ff", fontSize: 13 }}>#{q.query_id}</span>
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  {urgCfg && (
                    <span style={{
                      padding: "1px 6px", borderRadius: 20, fontSize: 10, fontWeight: 600,
                      background: urgCfg.pill, color: urgCfg.color, border: `1px solid ${urgCfg.color}`,
                    }}>
                      {urgCfg.label.split(" · ")[1]}
                    </span>
                  )}
                  <Tag style={{ margin: 0, fontSize: 10, padding: "0 5px", color: st.color, borderColor: st.color, background: `${st.color}18` }}>
                    {st.label}
                  </Tag>
                </div>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                {vehicleLabel && (
                  <span style={{ fontSize: 12, color: "#262626", display: "flex", alignItems: "center", gap: 4 }}>
                    <CarOutlined style={{ fontSize: 11, color: "#8c8c8c" }} />
                    {vehicleLabel}
                  </span>
                )}
                {assigneeName && (
                  <span style={{ fontSize: 11, color: "#8c8c8c", display: "flex", alignItems: "center", gap: 4 }}>
                    <Avatar size={14} style={{ backgroundColor: getAgentColor(assigneeName), fontSize: 8 }}>
                      {agentInitials(assigneeName)}
                    </Avatar>
                    {assigneeName}
                  </span>
                )}
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 4 }}>
                {q.parts_count > 0 && (
                  <span style={{ fontSize: 11, color: "#8c8c8c" }}>
                    {q.parts_count} {q.parts_count === 1 ? "part" : "parts"}
                  </span>
                )}
                {q.created_at && (
                  <span style={{ fontSize: 11, color: "#bfbfbf", display: "flex", alignItems: "center", gap: 3 }}>
                    <CalendarOutlined style={{ fontSize: 10 }} />
                    {dayjs(q.created_at).format("DD MMM YYYY")}
                  </span>
                )}
              </div>
            </div>
          );
        })
      )}
    </div>
  );

  return (
    <>
      <Drawer
        placement="left"
        width={440}
        open={open}
        onClose={onClose}
        destroyOnClose={false}
        mask={false}
        maskClosable={false}
        push={false}
        title={
          <div>
            <div style={{ fontWeight: 700 }}>Customer</div>
            <div style={{ fontSize: 12, color: "#8c8c8c", fontWeight: 400 }}>
              {customer?.customer_name || customer?.customer_mobile || ""}
            </div>
          </div>
        }
        footer={
          activeTab === "customer" ? (
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <Button onClick={onClose}>Cancel</Button>
              <Button type="primary" loading={saving} onClick={handleSave}>Save</Button>
            </div>
          ) : null
        }
        styles={{ body: { padding: 0 } }}
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          size="small"
          style={{ height: "100%" }}
          tabBarStyle={{ margin: 0, paddingLeft: 16, paddingRight: 16, borderBottom: "1px solid #f0f0f0" }}
          items={[
            {
              key: "customer",
              label: "Customer Data",
              children: (
                <div style={{ padding: "16px 24px", overflowY: "auto", height: "100%" }}>
                  {customerDataTab}
                </div>
              ),
            },
            {
              key: "queries",
              label: (
                <span>
                  Queries
                  {queriesList.length > 0 && (
                    <Badge count={queriesList.length} size="small" color="#8c8c8c" style={{ marginLeft: 6 }} />
                  )}
                </span>
              ),
              children: (
                <div style={{ padding: "16px 24px", overflowY: "auto", height: "100%" }}>
                  {queriesTab}
                </div>
              ),
            },
          ]}
        />
      </Drawer>

      <WorkflowTaskDrawer
        queryPk={selectedQueryPk}
        onClose={() => setSelectedQueryPk(null)}
      />
    </>
  );
}
